import { progressPct } from "@/lib/progress";
import { useMemo, useState, useEffect } from "react";
import { parseISO, isBefore, isAfter, addDays, differenceInCalendarDays } from "date-fns";
import { statusMeta, priorityMeta, phaseColor, HEALTH_COLORS, HEALTH_LABELS, STATUSES } from "@/lib/constants";
import api from "@/lib/api";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { AlertTriangle, CalendarClock, CheckCircle2, PlayCircle, PieChart as PieIcon, BarChart3, Activity } from "lucide-react";

function Section({ title, icon: Icon, tint, tasks, phaseColors, onOpenTask, empty }) {
  const colorOf = (p) => (phaseColors && phaseColors[p]) || phaseColor(p);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border" style={{ background: `${tint}0d` }}>
        <Icon className="h-4 w-4" style={{ color: tint }} />
        <span className="font-display font-bold">{title}</span>
        <span className="text-xs text-muted-foreground">{tasks.length}</span>
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground px-4 py-6 text-center">{empty}</p>
      ) : (
        <div className="divide-y divide-border">
          {tasks.map((t) => {
            const sm = statusMeta(t.status);
            const pm = priorityMeta(t.priority);
            return (
              <button key={t.id} onClick={() => onOpenTask(t)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/30 text-left" data-testid={`report-task-${t.id}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colorOf(t.phase) }} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground">{t.phase} · {t.assignee || "Unassigned"}</p>
                </div>
                {t.due_label && <span className="text-[11px] font-semibold shrink-0" style={{ color: tint }}>{t.due_label}</span>}
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: sm.color, background: `${sm.color}1a` }}>{sm.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ReportsView({ tasks, phaseColors, onOpenTask, projectId, progressStyle }) {
  const [cp, setCp] = useState(null);
  const [drill, setDrill] = useState(null);
  const [risks, setRisks] = useState([]);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/critical-path`).then(({ data }) => setCp(data)).catch(() => setCp(null));
    api.get(`/projects/${projectId}/risks`).then(({ data }) => setRisks(data)).catch(() => {});
    api.get(`/projects/${projectId}/action-items`).then(({ data }) => setActions(data)).catch(() => {});
  }, [projectId]);

  const { overdue, dueSoon, upcoming, done, total } = useMemo(() => {
    const now = new Date();
    const overdue = [], dueSoon = [], upcoming = [], done = [];
    tasks.forEach((t) => {
      if (t.status === "done") { done.push(t); return; }
      const end = t.end_date ? parseISO(t.end_date) : null;
      const start = t.start_date ? parseISO(t.start_date) : null;
      if (end && isBefore(end, now)) {
        overdue.push({ ...t, due_label: `${differenceInCalendarDays(now, end)}d overdue` });
      } else if (end && isBefore(end, addDays(now, 14))) {
        dueSoon.push({ ...t, due_label: `due in ${Math.max(0, differenceInCalendarDays(end, now))}d` });
      } else if (start && isAfter(start, now) && isBefore(start, addDays(now, 14))) {
        upcoming.push({ ...t, due_label: `starts in ${differenceInCalendarDays(start, now)}d` });
      }
    });
    overdue.sort((a, b) => parseISO(a.end_date) - parseISO(b.end_date));
    dueSoon.sort((a, b) => parseISO(a.end_date) - parseISO(b.end_date));
    upcoming.sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date));
    return { overdue, dueSoon, upcoming, done, total: tasks.length };
  }, [tasks]);

  const kpis = [
    { label: "Overdue", value: overdue.length, tint: "#EF4444", target: "overdue-section" },
    { label: "Due in 14 days", value: dueSoon.length, tint: "#F59E0B", target: "duesoon-section" },
    { label: "Starting soon", value: upcoming.length, tint: "#3B82F6", target: "upcoming-section" },
    { label: "Completed", value: `${done.length}/${total}`, tint: "#2563EB", target: "reports-charts" },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const healthData = useMemo(() => {
    const src = cp?.tasks || [];
    const counts = {};
    src.forEach((t) => { counts[t.health] = (counts[t.health] || 0) + 1; });
    return ["blue", "green", "yellow", "red", "gray"]
      .map((k) => ({ key: k, name: HEALTH_LABELS[k], value: counts[k] || 0, color: HEALTH_COLORS[k] }))
      .filter((d) => d.value > 0);
  }, [cp]);

  const statusData = useMemo(() =>
    STATUSES.map((s) => ({ name: s.label, count: tasks.filter((t) => !t.is_summary && t.status === s.key).length, color: s.color })),
  [tasks]);

  const phaseData = useMemo(() => {
    const src = tasks || [];
    const map = {};
    src.forEach((t) => {
      const p = t.phase || "Unphased";
      if (!map[p]) map[p] = { phase: p, items: [] };
      map[p].items.push(t);
    });
    return Object.values(map).map((m) => ({
      full: m.phase,
      phase: m.phase.length > 14 ? m.phase.slice(0, 13) + "…" : m.phase,
      avg: progressPct(m.items, progressStyle),
      color: (phaseColors && phaseColors[m.phase]) || phaseColor(m.phase),
    }));
  }, [tasks, phaseColors, progressStyle]);

  const blockers = useMemo(() => tasks.filter((t) => t.status === "blocked"), [tasks]);
  const offTrack = useMemo(() => (cp?.tasks || []).filter((t) => t.health === "red"), [cp]);

  const actionData = useMemo(() => {
    const b = { late: 0, pending: 0, completed: 0 };
    actions.forEach((a) => { b[a.bucket || "pending"] += 1; });
    return [
      { key: "late", name: "Late", value: b.late, color: "#EF4444" },
      { key: "pending", name: "Pending", value: b.pending, color: "#10B981" },
      { key: "completed", name: "Completed", value: b.completed, color: "#2563EB" },
    ].filter((d) => d.value > 0);
  }, [actions]);

  const riskData = useMemo(() => {
    const sev = { Low: 0, Moderate: 0, High: 0, Critical: 0 };
    const col = { Low: "#10B981", Moderate: "#F59E0B", High: "#F97316", Critical: "#EF4444" };
    risks.filter((r) => r.status !== "closed").forEach((r) => {
      const s = r.score || 0;
      const k = s >= 15 ? "Critical" : s >= 10 ? "High" : s >= 5 ? "Moderate" : "Low";
      sev[k] += 1;
    });
    return Object.entries(sev).map(([name, value]) => ({ name, value, color: col[name] })).filter((d) => d.value > 0);
  }, [risks]);

  return (
    <div className="space-y-6" data-testid="reports-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <button key={k.label} onClick={() => scrollTo(k.target)} className="border border-border rounded-lg p-5 text-left hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid={`report-kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}>
            <p className="overline text-muted-foreground flex items-center justify-between">{k.label}<span className="text-[10px] normal-case tracking-normal text-primary opacity-0 group-hover:opacity-100">view →</span></p>
            <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: k.tint }}>{k.value}</p>
          </button>
        ))}
      </div>

      {/* Visual dashboards */}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="reports-charts">
        <div className="border border-border rounded-lg p-5">
          <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><PieIcon className="h-4 w-4 text-primary" /> Health mix</h4>
          {healthData.length === 0 ? (
            <p className="text-xs text-muted-foreground py-16 text-center">Confirm progress to populate health.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={healthData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2} strokeWidth={0} cursor="pointer"
                  onClick={(d) => d?.payload && setDrill({ title: `${d.payload.name} tasks`, rows: (cp?.tasks || []).filter((t) => !t.is_summary && t.health === d.payload.key) })}>
                  {healthData.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-border rounded-lg p-5">
          <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-primary" /> Status breakdown</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusData} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} cursor="pointer"
                onClick={(d) => { const st = STATUSES.find((s) => s.label === d?.name); if (st) setDrill({ title: `${d.name} tasks`, rows: tasks.filter((t) => !t.is_summary && t.status === st.key) }); }}>
                {statusData.map((d, i) => <Cell key={d.name ?? i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-lg p-5">
          <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-primary" /> Avg % complete by phase</h4>
          {phaseData.length === 0 ? (
            <p className="text-xs text-muted-foreground py-16 text-center">No phase data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={phaseData} layout="vertical" margin={{ top: 4, right: 20, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="phase" width={90} tick={{ fontSize: 10 }} />
                <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} formatter={(v) => `${v}%`} />
                <Bar dataKey="avg" radius={[0, 6, 6, 0]} cursor="pointer"
                  onClick={(d) => d?.payload && setDrill({ title: `${d.payload.full} — tasks`, rows: tasks.filter((t) => !t.is_summary && (t.phase || "Unphased") === d.payload.full) })}>
                  {phaseData.map((d, i) => <Cell key={d.name ?? i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {drill && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDrill(null)} data-testid="reports-drill-overlay">
          <div className="bg-white rounded-xl border border-border shadow-2xl w-full max-w-lg max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="reports-drill-dialog">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
              <div>
                <h4 className="font-display font-extrabold text-base">{drill.title}</h4>
                <p className="text-xs text-muted-foreground">{drill.rows.length} task{drill.rows.length === 1 ? "" : "s"} · click one to open it</p>
              </div>
              <button className="text-muted-foreground hover:text-foreground text-xl leading-none" onClick={() => setDrill(null)} data-testid="reports-drill-close">×</button>
            </div>
            <div className="overflow-y-auto p-2">
              {drill.rows.length === 0 && <p className="text-sm text-muted-foreground p-4">No tasks here.</p>}
              {drill.rows.map((t) => (
                <button key={t.id} onClick={() => { setDrill(null); onOpenTask && onOpenTask(t); }}
                  className="w-full text-left flex items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-secondary/60" data-testid={`reports-drill-task-${t.id}`}>
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: (phaseColors && phaseColors[t.phase]) || phaseColor(t.phase || "") }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">{t.title}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">{[t.phase, t.assignee || t.responsible || "Unassigned", t.end_date ? `due ${t.end_date}` : null].filter(Boolean).join(" · ")}</span>
                  </span>
                  <span className="text-[11px] font-bold tabular-nums text-slate-600 shrink-0">{Math.round(t.percent_complete || 0)}%</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {(actionData.length > 0 || riskData.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="reports-charts-2">
          <div className="border border-border rounded-lg p-5">
            <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><PieIcon className="h-4 w-4 text-primary" /> Action items</h4>
            {actionData.length === 0 ? <p className="text-xs text-muted-foreground py-14 text-center">No action items yet.</p> : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={actionData} dataKey="value" nameKey="name" innerRadius={44} outerRadius={74} paddingAngle={2} strokeWidth={0}>
                    {actionData.map((d) => <Cell key={d.key} fill={d.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="border border-border rounded-lg p-5">
            <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-primary" /> Open risks by severity</h4>
            {riskData.length === 0 ? <p className="text-xs text-muted-foreground py-14 text-center">No open risks logged.</p> : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={riskData} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {riskData.map((d, i) => <Cell key={d.name ?? i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {(blockers.length > 0 || offTrack.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="border rounded-lg p-4" style={{ borderColor: "#EF444433", background: "#EF44440a" }} data-testid="reports-blockers">
            <p className="text-sm font-bold flex items-center gap-2" style={{ color: "#EF4444" }}><AlertTriangle className="h-4 w-4" /> Blockers ({blockers.length})</p>
            <div className="mt-2 space-y-1">
              {blockers.length === 0 ? <p className="text-xs text-muted-foreground">None 🎉</p> : blockers.map((t) => (
                <button key={t.id} onClick={() => onOpenTask(t)} className="block text-left text-xs hover:text-foreground text-muted-foreground w-full truncate" data-testid={`report-blocker-${t.id}`}>• {t.title} — {t.assignee || "Unassigned"}</button>
              ))}
            </div>
          </div>
          <div className="border rounded-lg p-4" style={{ borderColor: "#F59E0B33", background: "#F59E0B0a" }} data-testid="reports-offtrack">
            <p className="text-sm font-bold flex items-center gap-2" style={{ color: "#F59E0B" }}><Activity className="h-4 w-4" /> Off-track ({offTrack.length})</p>
            <div className="mt-2 space-y-1">
              {offTrack.length === 0 ? <p className="text-xs text-muted-foreground">None 🎉</p> : offTrack.map((t) => (
                <button key={t.id} onClick={() => onOpenTask(tasks.find((x) => x.id === t.id) || t)} className="block text-left text-xs hover:text-foreground text-muted-foreground w-full truncate" data-testid={`report-offtrack-${t.id}`}>• {t.title} — {t.percent_complete}% vs {t.expected_percent}% expected</button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div id="overdue-section"><Section title="Overdue" icon={AlertTriangle} tint="#EF4444" tasks={overdue} phaseColors={phaseColors} onOpenTask={onOpenTask} empty="Nothing overdue. 🎯" /></div>
      <div id="duesoon-section"><Section title="Due in the next 14 days" icon={CalendarClock} tint="#F59E0B" tasks={dueSoon} phaseColors={phaseColors} onOpenTask={onOpenTask} empty="No deadlines in the next two weeks." /></div>
      <div id="upcoming-section"><Section title="Starting soon" icon={PlayCircle} tint="#3B82F6" tasks={upcoming} phaseColors={phaseColors} onOpenTask={onOpenTask} empty="No tasks kicking off soon." /></div>
    </div>
  );
}
